import { uploadFileWithPreSignedUrl } from '../../api/preSignedUrlApi';
import { getConfig } from '../../env/envManager';
import {
  FileWithPEType,
  PreSignedUrlPEReq,
  PreSignedUrlProjectElementBatchReq,
  PreSignedUrlWorkBatchReq,
} from '../../dto/ReqDtoRepository';
import { createWorkBulk } from '../../api/workListApi';
import { createWorkDetailBulk } from '../../api/workDetailApi';
import { createProjectElementBulk } from '../../api/projectElementApi';
import { useBulkStore } from '../../store/WorkListStore';
import { useApiWrapperWithReturn } from './apiWrapperWithReturn';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { filterUnsupportedImageFiles } from '../../util/imageFileFormat';

export type PEBulkUploadResult = { peId: string; originUrl: string };

interface UseImageResult {
  createWorkBulk: (data: File[]) => Promise<{ workId: string; originUrl: string }[] | null>;
  createDetailBulk: (data: File[], workId: string) => Promise<{ workDetailId: string; originUrl: string }[] | null>;
  createPEBulk: (
    files: FileWithPEType[],
    projectId: string,
    projectElementListLength: number,
  ) => Promise<PEBulkUploadResult[] | null>;
  // OnBoarding 에서 사용
  createWorkBulkWithAui: (aui: string, data: File[]) => Promise<{ workId: string; originUrl: string }[] | null>;
}

export const useBulk = (): UseImageResult => {
  const config = getConfig();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const { addCurrent } = useBulkStore();
  const { apiWrapperWithReturn } = useApiWrapperWithReturn();

  const runCurrentBulkRequest = async <T,>(
    request: () => Promise<T>,
  ): Promise<T | null> => {
    if (!pageOwner) return null;
    const result = await apiWrapperWithReturn(
      () => runCurrentArchiveRequest(
        pageOwner.aui,
        request,
        (response) => response,
      ),
      'AWS',
    );
    return result ?? null;
  };

  // -------- Upload --------
  const uploadWorkImgToS3Bulk = async (files: File[]): Promise<{ workId: string; originUrl: string }[]> => {
    if (!pageOwner) return [];
    const bucketName = config.s3BucketName;
    const preSignedUrlBatchReq: PreSignedUrlWorkBatchReq = {
      files: files.map((file) => ({ fileName: file.name, fileType: file.type, fileSize: file.size })),
    };

    const response = await createWorkBulk(pageOwner.aui, preSignedUrlBatchReq);
    const results = response.data.items;

    const resultData: { workId: string; originUrl: string }[] = [];
    for (let i = 0; i < results.length; i++) {
      addCurrent();
      const preSignedItem = results[i];
      const file = files[i];
      // 여기서 AWS S3에 업로드 후
      await uploadFileWithPreSignedUrl(preSignedItem.preSignedUrl, file);

      resultData.push({
        workId: preSignedItem.id,
        originUrl: `https://${bucketName}.s3.amazonaws.com/${preSignedItem.key}`,
      });
    }
    return resultData;
  };

  const uploadDetailImgToS3Bulk = async (
    files: File[],
    workId: string,
  ): Promise<{ workDetailId: string; originUrl: string }[]> => {
    if (!pageOwner) return [];
    const bucketName = config.s3BucketName;
    const preSignedUrlBatchForDetailReq: PreSignedUrlWorkBatchReq = {
      files: files.map((file) => ({ fileName: file.name, fileType: file.type, fileSize: file.size })),
      workId,
    };

    const response = await createWorkDetailBulk(pageOwner.aui, preSignedUrlBatchForDetailReq);
    const results = response.data.items;

    const resultData: { workDetailId: string; originUrl: string }[] = [];
    for (let i = 0; i < results.length; i++) {
      addCurrent();
      const preSignedItem = results[i];
      const file = files[i];
      // 여기서 AWS S3에 업로드 후
      await uploadFileWithPreSignedUrl(preSignedItem.preSignedUrl, file);

      resultData.push({
        workDetailId: preSignedItem.id,
        originUrl: `https://${bucketName}.s3.amazonaws.com/${preSignedItem.key}`,
      });
    }
    return resultData;
  };

  const uploadPEImgToS3Bulk = async (
    aui: string,
    files: FileWithPEType[],
    projectId: string,
  ): Promise<PEBulkUploadResult[]> => {
    const bucketName = config.s3BucketName;
    const preSignedUrlBatchForProjectElementReq: PreSignedUrlProjectElementBatchReq = {
      projectId,
      files: files.map((entry, index) => ({
        fileName: entry.file?.name ?? '',
        fileType: entry.file?.type ?? '',
        fileSize: entry.file?.size ?? 0,
        projectElementType: entry.peType as PreSignedUrlPEReq['projectElementType'],
      })),
    };

    const response = await createProjectElementBulk(aui, preSignedUrlBatchForProjectElementReq);
    const results = response.data.items;
    if (results.length !== files.length) {
      throw new Error('PE_BULK_MISMATCH::Presigned item count does not match files');
    }

    const resultData: PEBulkUploadResult[] = [];
    for (let i = 0; i < results.length; i++) {
      const resultItem = results[i];
      const entry = files[i];

      if (!resultItem.preSignedUrl) {
        resultData.push({
          peId: resultItem.id,
          originUrl: '',
        });
        continue;
      }

      addCurrent();
      const file = entry.file;
      if (file) {
        await uploadFileWithPreSignedUrl(resultItem.preSignedUrl, file);
      }
      resultData.push({
        peId: resultItem.id,
        originUrl: resultItem.key ? `https://${bucketName}.s3.amazonaws.com/${resultItem.key}` : '',
      });
    }
    return resultData;
  };

  const uploadPEImgToS3BulkWithPageOwner = async (
    files: FileWithPEType[],
    projectId: string
  ): Promise<PEBulkUploadResult[]> => {
    if (!pageOwner) return [];
    return uploadPEImgToS3Bulk(pageOwner.aui, files, projectId);
  };

  const uploadWorkImgToS3BulkWithAui = async (aui: string, files: File[]): Promise<{ workId: string; originUrl: string }[]> => {
    const bucketName = config.s3BucketName;
    const preSignedUrlBatchReq: PreSignedUrlWorkBatchReq = {
      files: files.map((file) => ({ fileName: file.name, fileType: file.type, fileSize: file.size })),
    };

    const response = await createWorkBulk(aui, preSignedUrlBatchReq);
    const results = response.data.items;

    const resultData: { workId: string; originUrl: string }[] = [];
    for (let i = 0; i < results.length; i++) {
      addCurrent();
      const preSignedItem = results[i];
      const file = files[i];
      // 여기서 AWS S3에 업로드 후
      await uploadFileWithPreSignedUrl(preSignedItem.preSignedUrl, file);

      resultData.push({
        workId: preSignedItem.id,
        originUrl: `https://${bucketName}.s3.amazonaws.com/${preSignedItem.key}`,
      });
    }
    return resultData;
  };

  // -------- Handler --------
  const createWorkBulkHandler = (data: File[]): Promise<{ workId: string; originUrl: string }[] | null> => {
    if (filterUnsupportedImageFiles(data).length !== data.length) return Promise.resolve(null);
    return runCurrentBulkRequest(() => uploadWorkImgToS3Bulk(data));
  };

  const createDetailBulkHandler = async (
    data: File[],
    workId: string,
  ): Promise<{ workDetailId: string; originUrl: string }[] | null> => {
    if (filterUnsupportedImageFiles(data).length !== data.length) return null;
    return runCurrentBulkRequest(() =>
      uploadDetailImgToS3Bulk(data, workId),
    );
  };

  const createPEBulkHandler = (
    files: FileWithPEType[],
    projectId: string,
  ): Promise<PEBulkUploadResult[] | null> => {
    const selectedFiles = files.flatMap(({ file }) => file ? [file] : []);
    if (filterUnsupportedImageFiles(selectedFiles).length !== selectedFiles.length) return Promise.resolve(null);
    return runCurrentBulkRequest(
      () => uploadPEImgToS3BulkWithPageOwner(files, projectId),
    );
  };

  const createWorkBulkWithAuiHandler = (aui: string, data: File[]): Promise<{ workId: string; originUrl: string }[] | null> => {
    if (filterUnsupportedImageFiles(data).length !== data.length) return Promise.resolve(null);
    return apiWrapperWithReturn(() => uploadWorkImgToS3BulkWithAui(aui, data), 'AWS');
  };

  return {
    createWorkBulk: createWorkBulkHandler,
    createDetailBulk: createDetailBulkHandler,
    createPEBulk: createPEBulkHandler,
    createWorkBulkWithAui: createWorkBulkWithAuiHandler,
  };
};
